// =============================================================================
// TrackerGantt.jsx  (v5)
//
// Melhorias:
//   1. Legenda corrigida — cores da legenda = cores das barras (TECH_COLOR)
//   2. Auto-scroll para TODAY ao montar
//   3. Controlo de zoom: 1W / 1M / 6M / 1Y / All
//   4. Altura mínima do SVG (não colapsa com poucas actividades)
// =============================================================================

import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Chip, Divider, Slider } from '@mui/material';
import TimelineIcon  from '@mui/icons-material/Timeline';
import PersonIcon    from '@mui/icons-material/Person';
import GroupsIcon    from '@mui/icons-material/Groups';
import CalendarIcon  from '@mui/icons-material/CalendarToday';
import LayersIcon    from '@mui/icons-material/Layers';
import ReplayIcon    from '@mui/icons-material/Replay';

// ── Layout ────────────────────────────────────────────────────────────────────
const ROW_H      = 48;
const LABEL_W    = 220;
const HEADER_H   = 52;
const BAR_R      = 5;
const BAR_PAD    = 10;
const MIN_BAR    = 8;
const MIN_SVG_H  = 220;   // altura mínima do SVG

// ── Paleta categórica BI ──────────────────────────────────────────────────────
const TECH_COLOR = {
  '2G': '#4E79A7',
  '3G': '#59A14F',
  '4G': '#E07B39',
  '5G': '#8B6BB1',
};
const DEFAULT_BAR = '#9BA3AF';

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return new Date(`${slash[3]}-${slash[1]}-${slash[2]}T12:00:00Z`);
  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, y, a, b] = dash;
    const iso = Number(a) > 12 ? `${y}-${b}-${a}` : `${y}-${a}-${b}`;
    return new Date(`${iso}T12:00:00Z`);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const fmt = (d) => {
  if (!d) return null;
  const y  = d.getUTCFullYear();
  const m  = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const buildTicks = (minMs, maxMs, pxPerDay) => {
  const ticks = [];
  const cur = new Date(minMs);
  cur.setUTCDate(1);

  // Escolher granularidade do eixo baseada em px por dia
  let step; // 'day' | 'week' | 'month'
  if (pxPerDay >= 30) step = 'day';
  else if (pxPerDay >= 8) step = 'week';
  else step = 'month';

  if (step === 'month') {
    while (cur.getTime() <= maxMs + 86400000 * 15) {
      ticks.push({ date: new Date(cur), step: 'month' });
      cur.setUTCMonth(cur.getUTCMonth() + 1);
    }
  } else if (step === 'week') {
    // Avança para a próxima segunda-feira
    const dow = cur.getUTCDay();
    cur.setUTCDate(cur.getUTCDate() + ((8 - dow) % 7));
    while (cur.getTime() <= maxMs) {
      ticks.push({ date: new Date(cur), step: 'week' });
      cur.setUTCDate(cur.getUTCDate() + 7);
    }
  } else {
    cur.setTime(minMs);
    while (cur.getTime() <= maxMs) {
      ticks.push({ date: new Date(cur), step: 'day' });
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
  }
  return ticks;
};

const fmtTick = (date, step) => {
  if (step === 'month') return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  if (step === 'week')  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  return String(date.getUTCDate()).padStart(2, '0');
};

// ── Tooltip rico ──────────────────────────────────────────────────────────────
const RichTooltip = ({ entry, mouseX, mouseY }) => {
  const barColor  = TECH_COLOR[entry.technology] ?? DEFAULT_BAR;
  const startDate = fmt(parseDate(entry.startDate));
  const endDate   = fmt(parseDate(entry.endDate));
  const phases    = (entry.phases ?? []).filter((p) => !p.label?.toLowerCase().startsWith('rollback'));
  const rollbacks = (entry.phases ?? []).filter((p) =>  p.label?.toLowerCase().startsWith('rollback'));

  return (
    <Box sx={{
      position: 'fixed', left: mouseX + 18, top: mouseY - 16,
      zIndex: 9999, pointerEvents: 'none', width: 280,
      bgcolor: 'background.paper', borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      border: '1px solid', borderColor: 'divider', overflow: 'hidden',
    }}>
      <Box sx={{ px: 1.75, py: 1.25, bgcolor: barColor, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', lineHeight: 1.3, flex: 1 }}>
          {entry.title}
        </Typography>
        {entry.technology && (
          <Chip label={entry.technology} size="small" sx={{
            height: 18, fontSize: '0.65rem', fontWeight: 800,
            bgcolor: 'rgba(255,255,255,0.25)', color: 'white', flexShrink: 0,
          }} />
        )}
      </Box>

      <Box sx={{ px: 1.75, py: 1.25 }}>
        {(entry.vendor || entry.feature) && (
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 1 }}>
            {[entry.vendor, entry.feature].filter(Boolean).join(' · ')}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <CalendarIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
          <Typography sx={{ fontSize: '0.74rem', color: 'text.primary' }}>
            {startDate ?? '—'}
            <Box component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>→</Box>
            {endDate ?? <Box component="span" sx={{ color: 'warning.main', fontStyle: 'italic' }}>ongoing</Box>}
          </Typography>
        </Box>

        {entry.teamName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <GroupsIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '0.74rem' }}>{entry.teamName}</Typography>
          </Box>
        )}

        {entry.ownerNames?.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
            <PersonIcon sx={{ fontSize: 13, color: 'text.disabled', mt: '2px' }} />
            <Typography sx={{ fontSize: '0.74rem', lineHeight: 1.5 }}>
              {entry.ownerNames.join(', ')}
            </Typography>
          </Box>
        )}

        {phases.length > 0 && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <LayersIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Phases</Typography>
            </Box>
            {phases.map((p, idx) => (
              <Box key={idx} sx={{ mb: 0.5, pl: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.72rem' }}>{p.label ?? `Phase ${idx + 1}`}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1, flexShrink: 0 }}>{fmt(parseDate(p.date)) ?? '—'}</Typography>
                </Box>
                {p.crq && (
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontFamily: 'monospace' }}>
                    {p.crq}
                  </Typography>
                )}
              </Box>
            ))}
          </>
        )}

        {rollbacks.length > 0 && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <ReplayIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rollbacks</Typography>
            </Box>
            {rollbacks.map((r, idx) => (
              <Box key={idx} sx={{ mb: 0.5, pl: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.72rem' }}>{r.label ?? `Rollback ${idx + 1}`}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1, flexShrink: 0 }}>{fmt(parseDate(r.date)) ?? '—'}</Typography>
                </Box>
                {r.crq && (
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontFamily: 'monospace' }}>
                    {r.crq}
                  </Typography>
                )}
              </Box>
            ))}
          </>
        )}

        {entry.comments && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Typography sx={{ fontSize: '0.71rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
              {entry.comments.length > 120 ? entry.comments.slice(0, 120) + '…' : entry.comments}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────
const TrackerGantt = ({ entries }) => {
  const containerRef = useRef(null);
  const [tooltip,    setTooltip]   = useState(null);
  // sliderRange: [0..100, 0..100] — percentagem sobre o intervalo total dos dados
  const [sliderRange, setSliderRange] = useState([0, 100]);

  const rows = useMemo(
    () => entries.filter((e) => parseDate(e.startDate)),
    [entries]
  );

  // ── Limites absolutos dos dados ───────────────────────────────────────────
  // sliderMin = startDate mais antigo
  // sliderMax = endDate mais recente, ou today se todos forem null
  const { sliderMin, sliderMax } = useMemo(() => {
    if (!rows.length) return { sliderMin: Date.now(), sliderMax: Date.now() + 86400000 * 30 };

    const starts = rows.map((e) => parseDate(e.startDate)?.getTime()).filter(Boolean);
    const ends   = rows.map((e) => parseDate(e.endDate)?.getTime()).filter(Boolean);

    const rawMin = Math.min(...starts);
    const rawMax = ends.length ? Math.max(...ends) : Date.now();

    // Pequeno padding visual (5 dias antes, 10 depois)
    return {
      sliderMin: rawMin - 86400000 * 5,
      sliderMax: rawMax + 86400000 * 10,
    };
  }, [rows]);

  // Reset do slider quando os dados mudam
  useEffect(() => { setSliderRange([0, 100]); }, [sliderMin, sliderMax]);

  // ── Janela temporal activa (controlada pelo slider) ───────────────────────
  const totalMs = sliderMax - sliderMin;
  const minMs   = sliderMin + (sliderRange[0] / 100) * totalMs;
  const maxMs   = sliderMin + (sliderRange[1] / 100) * totalMs;

  // Labels do slider (datas)
  const sliderFmt = (pct) => {
    const d = new Date(sliderMin + (pct / 100) * totalMs);
    return fmt(d);
  };

  // ── Dimensões ─────────────────────────────────────────────────────────────
  const pxPerDay = Math.max(600 / ((maxMs - minMs) / 86400000), 1);
  const chartW   = Math.max(((maxMs - minMs) / 86400000) * pxPerDay, 600);
  const svgW     = LABEL_W + chartW;
  const svgH     = Math.max(HEADER_H + rows.length * ROW_H + 8, MIN_SVG_H);
  const msToX    = useCallback((ms) => LABEL_W + ((ms - minMs) / (maxMs - minMs)) * chartW, [minMs, maxMs, chartW]);
  const ticks    = useMemo(() => buildTicks(minMs, maxMs, pxPerDay), [minMs, maxMs, pxPerDay]);

  // ── Auto-scroll para TODAY ────────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const todayX = msToX(Date.now());
    el.scrollLeft = Math.max(0, todayX - el.clientWidth * 0.33);
  }, [msToX]);

  if (!rows.length) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            No activities with a start date to display in the Gantt chart.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const today     = Date.now();
  const todayX    = msToX(today);
  const showToday = today >= minMs && today <= maxMs;

  return (
    <>
      <Paper variant="outlined" sx={{ borderRadius: 2, mt: 3, overflow: 'hidden' }}>

        {/* ── Cabeçalho ────────────────────────────────────────────────── */}
        <Box sx={{
          px: 2.5, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'action.hover',
        }}>
          <TimelineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={700} letterSpacing={-0.2}>
            Gantt Chart
          </Typography>
          <Chip
            label={`${rows.length} ${rows.length === 1 ? 'activity' : 'activities'}`}
            size="small"
            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, ml: 0.5 }}
          />
          <Box sx={{ flex: 1 }} />

          {/* ── Slider de intervalo ──────────────────────────────────────── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 320 }}>
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {sliderFmt(sliderRange[0])}
            </Typography>
            <Slider
              value={sliderRange}
              onChange={(_, v) => setSliderRange(v)}
              size="small"
              disableSwap
              sx={{ flex: 1, mx: 0.5 }}
            />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {sliderFmt(sliderRange[1])}
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* ── Legenda — usa exactamente TECH_COLOR ───────────────────── */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {Object.entries(TECH_COLOR).map(([tech, color]) => (
              <Box key={tech} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: color }} />
                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                  {tech}
                </Typography>
              </Box>
            ))}
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: DEFAULT_BAR }} />
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                Other
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── SVG ──────────────────────────────────────────────────────── */}
        <Box ref={containerRef} sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
          <svg width={svgW} height={svgH} style={{ display: 'block', userSelect: 'none' }}>

            {/* Zebra */}
            {rows.map((_, i) => (
              <rect key={i}
                x={0} y={HEADER_H + i * ROW_H}
                width={svgW} height={ROW_H}
                fill={i % 2 === 0 ? '#ffffff' : '#f9fafb'}
              />
            ))}

            {/* Grid vertical */}
            {ticks.map((tick, i) => {
              const x = msToX(tick.date.getTime());
              if (x < LABEL_W || x > svgW) return null;
              return <line key={i} x1={x} y1={HEADER_H} x2={x} y2={svgH} stroke="#e5e7eb" strokeWidth={1} />;
            })}

            {/* Today */}
            {showToday && (
              <>
                <line x1={todayX} y1={HEADER_H - 4} x2={todayX} y2={svgH}
                  stroke="#EF4444" strokeWidth={1.5} strokeDasharray="4 3" />
                <rect x={todayX - 18} y={2} width={36} height={16} rx={3} fill="#EF4444" />
                <text x={todayX} y={13} textAnchor="middle"
                  fill="white" fontSize={9} fontFamily="Arial" fontWeight="bold">TODAY</text>
              </>
            )}

            {/* Cabeçalho temporal */}
            {ticks.map((tick, i) => {
              const x = msToX(tick.date.getTime());
              if (x < LABEL_W) return null;
              return (
                <text key={i} x={x + 4} y={HEADER_H - 10} fontSize={10} fill="#9ca3af" fontFamily="Arial">
                  {fmtTick(tick.date, tick.step)}
                </text>
              );
            })}

            {/* Divisores */}
            <line x1={LABEL_W} y1={0} x2={LABEL_W} y2={svgH} stroke="#e5e7eb" strokeWidth={1} />
            <line x1={0} y1={HEADER_H} x2={svgW} y2={HEADER_H} stroke="#e5e7eb" strokeWidth={1} />

            {/* Cabeçalho da coluna de nomes */}
            <rect x={0} y={0} width={LABEL_W} height={HEADER_H} fill="#f9fafb" />
            <text x={12} y={HEADER_H / 2 + 4}
              fontSize={10} fontFamily="Arial" fontWeight="700"
              fill="#9ca3af" letterSpacing="1">
              ACTIVITIES
            </text>

            {/* ══ LINHAS ══ */}
            {rows.map((entry, i) => {
              const y        = HEADER_H + i * ROW_H;
              const barColor = TECH_COLOR[entry.technology] ?? DEFAULT_BAR;
              const startMs  = parseDate(entry.startDate)?.getTime();
              const endMs    = parseDate(entry.endDate)?.getTime() ?? null;
              const isOpen   = !endMs;

              // Clamp barras ao viewport visível (evita barras infinitas no SVG)
              const rawX1 = msToX(startMs);
              const rawX2 = isOpen ? msToX(Date.now()) : Math.max(msToX(endMs), rawX1 + MIN_BAR);
              const barX1 = Math.max(rawX1, LABEL_W);
              const barX2 = Math.min(rawX2, svgW - 4);
              const barW  = Math.max(barX2 - barX1, 0);
              const barY  = y + BAR_PAD;
              const barH  = ROW_H - BAR_PAD * 2;
              const midY  = y + ROW_H / 2;

              // Actividade fora do viewport — não renderizar barra
              const outOfView = rawX2 < LABEL_W || rawX1 > svgW;

              return (
                <g key={entry.id}>
                  <clipPath id={`cl-lbl-${i}`}>
                    <rect x={0} y={y} width={LABEL_W - 10} height={ROW_H} />
                  </clipPath>

                  {/* Nome */}
                  <text x={8} y={y + ROW_H / 2 + 4}
                    fontSize={11} fontFamily="Arial" fill="#374151"
                    clipPath={`url(#cl-lbl-${i})`}>
                    {entry.title}
                  </text>

                  {!outOfView && barW > 0 && (
                    <>
                      {/* Barra */}
                      <rect
                        x={barX1} y={barY} width={barW} height={barH}
                        rx={BAR_R} fill={barColor} opacity={0.85}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => setTooltip({ entry, mouseX: e.clientX, mouseY: e.clientY })}
                        onMouseMove={(e)  => setTooltip((prev) => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : prev)}
                        onMouseLeave={()  => setTooltip(null)}
                      />

                      <clipPath id={`cl-bar-${i}`}>
                        <rect x={barX1} y={barY} width={barW - 4} height={barH} />
                      </clipPath>

                      {/* Tech label */}
                      {barW > 60 && (
                        <text x={barX1 + 7} y={barY + barH / 2 + 4}
                          fontSize={10} fontFamily="Arial" fontWeight="600"
                          fill="white" style={{ pointerEvents: 'none' }}
                          clipPath={`url(#cl-bar-${i})`}>
                          {entry.technology}
                        </text>
                      )}

                      {/* Seta "ongoing" — posicionada no fim da barra (TODAY), cor sólida */}
                      {isOpen && (
                        <polygon
                          points={`${barX2 + 10},${midY} ${barX2},${barY} ${barX2},${barY + barH}`}
                          fill={barColor} opacity={1} style={{ pointerEvents: 'none' }}
                        />
                      )}
                    </>
                  )}

                  {/* Separador */}
                  <line x1={0} y1={y + ROW_H} x2={svgW} y2={y + ROW_H} stroke="#f3f4f6" strokeWidth={1} />
                </g>
              );
            })}
          </svg>
        </Box>
      </Paper>

      {tooltip && (
        <RichTooltip entry={tooltip.entry} mouseX={tooltip.mouseX} mouseY={tooltip.mouseY} />
      )}
    </>
  );
};

export default TrackerGantt;